import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faTrashCan,
  faGripDotsVertical,
} from "@fortawesome/pro-solid-svg-icons";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AdminListCard, type BatchAction } from "@components/CatalogIndex";
import { conditionSummary, itemTypeLabel, type Recommendation } from "./types";
import "./style.css";

export interface RecommendationsListProps {
  rows: Recommendation[];
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  onAdd: () => void;
  onEdit: (rec: Recommendation) => void;
  onBatchDelete: () => void;
  onReorder: (activeId: string, overId: string) => void;
  itemName: (rec: Recommendation) => string;
}

const Row = ({
  rec,
  selectedIds,
  setSelectedIds,
  onEdit,
  itemName,
}: {
  rec: Recommendation;
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  onEdit: (rec: Recommendation) => void;
  itemName: (rec: Recommendation) => string;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: `rec:${rec.id}` });
  const checked = selectedIds.includes(rec.id);
  const name = itemName(rec) || `Item #${rec.itemId}`;
  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <td className="rec-grip" {...attributes} {...listeners}>
        <FontAwesomeIcon icon={faGripDotsVertical} />
      </td>
      <td>
        <input
          type="checkbox"
          style={{ accentColor: "#418172" }}
          aria-label={`Select recommendation ${name}`}
          checked={checked}
          onChange={() =>
            setSelectedIds(
              checked
                ? selectedIds.filter((x) => x !== rec.id)
                : [...selectedIds, rec.id]
            )
          }
        />
      </td>
      <td>
        <button
          type="button"
          className="adminListEditLink rec-edit-link"
          onClick={() => onEdit(rec)}
        >
          {name}
          <FontAwesomeIcon
            icon={faPencil}
            className="adminListEditLink__icon"
          />
        </button>
      </td>
      <td>{itemTypeLabel(rec.itemType)}</td>
      <td>{rec.quantityValue}</td>
      <td>{conditionSummary(rec)}</td>
    </tr>
  );
};

export const RecommendationsList = ({
  rows,
  selectedIds,
  setSelectedIds,
  onAdd,
  onEdit,
  onBatchDelete,
  onReorder,
  itemName,
}: RecommendationsListProps): JSX.Element => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder(
      String(active.id).replace("rec:", ""),
      String(over.id).replace("rec:", "")
    );
  };

  const batchActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: onBatchDelete,
      variant: "danger",
    },
  ];

  return (
    <AdminListCard
      title="Survey Recommendations"
      bare
      isEmpty={rows.length === 0}
      emptyMessage="No recommendations yet."
      onNew={onAdd}
      newButtonLabel="Add Recommendation"
      batchActions={batchActions}
      selectedCount={selectedIds.length}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <table className="recommendations-table">
          <thead>
            <tr>
              <th />
              <th className="select-col" />
              <th>Item</th>
              <th>Type</th>
              <th>Quantity</th>
              <th>Applies When</th>
            </tr>
          </thead>
          <SortableContext
            items={rows.map((r) => `rec:${r.id}`)}
            strategy={verticalListSortingStrategy}
          >
            <tbody>
              {rows.map((rec) => (
                <Row
                  key={rec.id}
                  rec={rec}
                  selectedIds={selectedIds}
                  setSelectedIds={setSelectedIds}
                  onEdit={onEdit}
                  itemName={itemName}
                />
              ))}
            </tbody>
          </SortableContext>
        </table>
      </DndContext>
    </AdminListCard>
  );
};

export default RecommendationsList;
